import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { sachetIcons, sachetTypeConverted } from '@constants/common';
import { KTSVG, toAbsoluteUrl, formatMoney } from '@helpers/index';
import { getVariantProductInfoSelector } from '@modules/products/core/selectors';
import {
  getCurrencyCodeSelector,
  getDefaultSubscriptionSelector,
  getDiscountSelector,
  getPetSelector,
} from '@modules/subscriptions/core/selectors';
import { formatDiscountTitle } from '@modules/subscriptions/core/discountLabel';
import { useClickOutside } from '@shared/index';

export const SubscriptionReactivationRecipes = () => {
  const intl = useIntl();
  const pet = useSelector(getPetSelector);
  const defaultSubscription = useSelector(getDefaultSubscriptionSelector);
  const productInfo = useSelector(getVariantProductInfoSelector);
  const currencyCode = useSelector(getCurrencyCodeSelector);
  const discount = useSelector(getDiscountSelector);

  const [isDropdownOpen, setIsDropdownOpen] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsDropdownOpen(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const ref = useRef<HTMLDivElement | null>(null);

  useClickOutside(ref, () => {
    setIsDropdownOpen(false);
  });

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <div className="subscription-reactivation-recipes">
      <button className="subscription-reactivation-dropdown " onClick={handleDropdownToggle}>
        {/* The possessive was `pet?.gender === 'f' ? 'ihrer' : 'seiner'`. As an ICU
            select, French can ignore the distinction — "sa prochaine commande" agrees
            with the noun, not the owner. */}
        <FormattedMessage
          id="SUBSCRIPTION.REACTIVATE.NEXT_ORDER_ITEMS"
          values={{ name: pet?.name, gender: pet?.gender ?? '' }}
        />{' '}
        <span className={`${!isDropdownOpen ? '' : 'rotate-180'} transition-transform`}>
          <KTSVG path={toAbsoluteUrl('/media/svg/general/down-arrow.svg')} />
        </span>
      </button>

      {isDropdownOpen && (
        <div>
          <p className="subscription-reactivation-dropdown-title">Mehrere Sorten</p>
          <ul className="grid grid-cols-2 xl:grid-cols-3 items-center gap-[2rem] md:gap-[3rem] text-left subscription-reactivation-dropdown-items">
            {productInfo?.sachets?.map((sachet: any, index: any) => {
              if (sachet.quantity === 0) return null;
              const sachetType = sachetTypeConverted[sachet.type] || sachet.type;
              const sachetIcon = sachetIcons[sachet.type.toLowerCase()];
              return (
                <li
                  className="flex gap-[0.5rem] md:gap-[1rem] items-center sachet-list"
                  key={index}
                  data-key={sachet.type}>
                  {sachetIcon && <img src={sachetIcon} alt={sachetType} className="sachet-icon" />}
                  <div className="text-left">
                    <span className="sachet-list-text">{sachetType}</span> <br />
                    <span className="sachet-list-content">
                      {sachet.quantity}x{sachet.weight.value}
                      {sachet.weight.unit}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="dashed-border"></div>
        </div>
      )}

      {discount && (
        <div className="subscription-reactivation-discount">
          <p>
            <FormattedMessage id="DISCOUNT.LABEL" />
          </p>
          <span>{formatDiscountTitle(intl, discount?.title, currencyCode)} </span>
        </div>
      )}

      <div className="subscription-reactivation-price">
        <div className="subscription-reactivation-price-info">
          SUMME
          <div>
            <FormattedMessage id="GLOBAL.INCL_VAT" />
          </div>
        </div>

        <div className="subscription-reactivation-price-summary">
          {/* These two were the only price displays without fraction-digit options,
              so they rendered "EUR 12,5" where every other one shows "EUR 12,50".
              formatMoney pins both to two decimals. */}
          {discount && (
            <p>{formatMoney(defaultSubscription?.totalLineItemPrice, currencyCode, intl.locale)}</p>
          )}
          <span>
            {formatMoney(
              defaultSubscription?.totalLineItemDiscountedPrice,
              currencyCode,
              intl.locale,
            )}
          </span>
        </div>
      </div>
    </div>
  );
};
